import { Alert, Card, Descriptions, message, Space, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listFeatureFlags,
  updateFeatureFlag,
  type FeatureFlagDto,
} from '../../api/feature-flags';

const { Title, Paragraph, Text } = Typography;

function formatTime(ms: number): string {
  if (!ms) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

export default function FeatureFlagsPage() {
  const qc = useQueryClient();
  const listQuery = useQuery({
    queryKey: ['feature-flags'],
    queryFn: listFeatureFlags,
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { flagKey: string; enabled: boolean }) =>
      updateFeatureFlag(vars.flagKey, vars.enabled),
    onSuccess: () => {
      message.success('功能开关已更新');
      qc.invalidateQueries({ queryKey: ['feature-flags'] });
    },
    onError: (e: { response?: { data?: { message?: string } }; message?: string }) => {
      message.error(e.response?.data?.message ?? e.message ?? '更新失败');
    },
  });

  const columns: ColumnsType<FeatureFlagDto> = [
    {
      title: '功能',
      dataIndex: 'name',
      render: (name, row) => (
        <Space direction="vertical" size={2}>
          <Text strong>{name}</Text>
          <Text type="secondary">{row.description}</Text>
          <Text code>{row.flagKey}</Text>
        </Space>
      ),
    },
    {
      title: '当前状态',
      dataIndex: 'enabled',
      width: 140,
      render: (enabled: boolean) => (
        enabled ? <Tag color="green">已开启</Tag> : <Tag>已关闭</Tag>
      ),
    },
    {
      title: '最后操作',
      width: 240,
      render: (_, row) => (
        <Descriptions column={1} size="small">
          <Descriptions.Item label="操作者">{row.updatedBy || '-'}</Descriptions.Item>
          <Descriptions.Item label="时间">{formatTime(row.updatedAt)}</Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      title: '操作',
      width: 160,
      render: (_, row) => (
        <PermissionGate point={PERMISSIONS.CONFIG_UPDATE}>
          <Switch
            checked={row.enabled}
            checkedChildren="开启"
            unCheckedChildren="关闭"
            loading={updateMutation.isPending && updateMutation.variables?.flagKey === row.flagKey}
            onChange={(checked) => updateMutation.mutate({ flagKey: row.flagKey, enabled: checked })}
          />
        </PermissionGate>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ marginBottom: 8 }}>功能开关</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          用于控制小程序端可远程开启/关闭的运营功能。当前打印服务默认关闭，产品上线后可在此开启。
        </Paragraph>
      </div>

      <Alert
        type="warning"
        showIcon
        message="关闭打印服务后，小程序将隐藏按需印制卡片和“我的-打印订单”入口。"
        description="该操作会写入后台审计日志；小程序端读取远程配置后生效，无需重新发布。"
      />

      <Card>
        <Table
          rowKey="flagKey"
          loading={listQuery.isLoading}
          dataSource={listQuery.data?.items ?? []}
          columns={columns}
          pagination={false}
        />
      </Card>
    </Space>
  );
}

